import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Loading from "../loading/loading";
import "../common/style.css";
import { employeeFormConfig, employeeFormTabs } from "../../form/FormData";
import DynamicForm from "../../form/DynamicForm";
import {
  downloadFileForm,
  getDepartmentList,
  getDesignationList,
  getStateList,
  updateEmployeeOpen,
  uploadFileForm,
  validateFormLink,
} from "../http/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function EditEmployeeOpen() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref"); // get form link reference
  const employeeFromState = location.state?.employee || null;

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [states, setStates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [files, setFiles] = useState({});
  const [employee, setEmployee] = useState(employeeFromState);
  const [error, setError] = useState(null);

  // Fetch static data
  useEffect(() => {
    getStateList().then((res) => setStates(res.data));
    getDepartmentList().then((res) => setDepartments(res.data));
    getDesignationList().then((res) => setDesignations(res.data));
  }, []);

  // Validate ref link and fetch employee
  useEffect(() => {
    if (!employee && ref) {
      setLoading(true);
      validateFormLink(ref)
        .then((res) => {
          console.log("res.data " + res.data);
          if (res.data) setEmployee(res.data);
          else setError("Invalid link, please contact HR Admin");
        })
        .catch((err) => {
          console.error(err);
          setError("Invalid link, please contact HR Admin");
        })
        .finally(() => setLoading(false));
    } else if (!employee && !ref) {
      setError("Invalid link, please contact HR Admin");
    }
  }, [ref, employee]);

  // ✅ Memoized initial form data (prevents ESLint warning)
  const initialFormData = useMemo(
    () => ({
      id: null,
      photo: null,
      name: "",
      dateOfBirth: "",
      gender: "",
      joiningDate: "",
      resignationDate: "",
      contractDate: "",
      bloodGroup: "",
      designation: "",
      department: "",
      reportingManager: "",
      personalMobileNo: "",
      officeMobileNo: "",
      personalEmail: "",
      officeEmail: "",
      permanentAddress: "",
      permanentCity: "",
      permanentState: "",
      permanentPinCode: "",
      mailingAddress: "",
      mailingCity: "",
      mailingState: "",
      mailingPinCode: "",
      panNumber: "",
      panCardDocument: null,
      aadhaarNumber: "",
      aadhaarCardDocument: null,
      cvDocument: null,
      contractDocument: null,
      lastQualification: "",
      lastQualificationCertificateDocument: null,
      passportDocument: null,
      drivingLicenseDocument: null,
      bankName: "",
      accountNumber: "",
      ifscCode: "",
      roles: "",
      pfUan: "",
      esiNumber: "",
      password: "",
      branchName: "",
      emergencyContacts: [{ name: "", relationship: "", mobileNumber: "" }],
      salary: [
        {
          basicSalary: 0,
          hra: 0,
          da: 0,
          tdsDeduction: 0,
          professionalTax: 0,
          otherAllowance: 0,
          pfEmployeeContribution: 0,
          pfEmployerContribution: 0,
          esiContribution: 0,
          effectiveDate: "",
        },
      ],
    }),
    [] // ✅ Keeps the same reference between renders
  );

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  // Populate formData when employee is loaded
  useEffect(() => {
    if (employee) {
      setFormData({
        ...initialFormData,
        ...employee,
        emergencyContacts:
          employee.emergencyContacts?.length > 0
            ? employee.emergencyContacts
            : initialFormData.emergencyContacts,
        salary:
          employee.salary?.length > 0
            ? employee.salary
            : initialFormData.salary,
      });
    }
  }, [employee, initialFormData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const validateForm = () => {
  //   const newErrors = {};
  //   employeeFormConfig.forEach((section) => {
  //     section.fields.forEach((field) => {
  //       const value = formData[field.name];
  //       if (field.required && !value) {
  //         newErrors[field.name] =
  //           field.errorMessage || "This field is required";
  //       }
  //     });
  //   });
  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };

  //

  const validateForm = (currentTabId = null) => {
    const newErrors = { ...errors }; // Start with existing errors

    // Filter sections to validate only current tab if specified
    const sectionsToValidate = currentTabId
      ? employeeFormConfig.filter((section) => section.id === currentTabId)
      : employeeFormConfig;

    sectionsToValidate.forEach((section) => {
      section.fields.forEach((field) => {
        const value = formData[field.name];

        // Clear existing error for this field if we're validating current tab
        if (currentTabId) {
          delete newErrors[field.name];
        }

        if (field.required && !value) {
          newErrors[field.name] =
            field.errorMessage || "This field is required";
        }
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    //if (!validateForm()) return;

    if (!validateForm()) {
      // Validate all tabs
      toast.error("Please fix all validation errors before submitting.");
      return;
    }
    setLoading(true);

    try {
      const payload = { ...formData };
      if (formData.id) {
        console.log("formData.id " + formData.id);
        await updateEmployeeOpen(ref, payload);
        toast.success("Employee updated successfully!");
      } else {
        toast.error("Unexpected error while saving the employee.");
      }
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error(
        error.response?.data?.message ||
          "Unexpected error while saving the employee."
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading />;

  if (error) {
    return (
      <div
        className="invalid-link"
        style={{ textAlign: "center", marginTop: "50px" }}
      >
        <ToastContainer position="top-right" autoClose={5000} />
        <h3 style={{ color: "red" }}>{error}</h3>
      </div>
    );
  }

  const handleFileUpload = async (fieldName, file) => {
    try {
      const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, "");
      const uniqueFileName = `${timestamp}_${file.name}`;
      const uniqueFile = new File([file], uniqueFileName, { type: file.type });

      const formDataObj = new FormData();
      formDataObj.append("file", uniqueFile);
      formDataObj.append("keyName", uniqueFile.name);

      const res = await uploadFileForm(formDataObj, ref);
      const savedFileName = res.data?.fileName || uniqueFile.name;

      setFormData((prev) => ({ ...prev, [fieldName]: savedFileName }));

      setFiles((prev) => ({
        ...prev,
        [fieldName]: {
          file: uniqueFile,
          previewUrl: file.type.startsWith("image/")
            ? URL.createObjectURL(uniqueFile)
            : null,
        },
      }));

      toast.success(`${uniqueFile.name} uploaded successfully`);
    } catch (err) {
      console.error("File upload failed:", err);
      toast.error("Failed to upload file. Please try again.");
    }
  };

  const handleFileDownload = async (fileName) => {
    try {
      const fileBlob = await downloadFileForm(fileName, ref);
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(new Blob([fileBlob.data]));
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed", err);
      toast.error("Failed to download file.");
    }
  };

  return (
    <div className="ShowEmployee">
      <ToastContainer position="top-right" autoClose={3000} />
      <div id="layoutSidenav">
        <div id="layoutSidenav_content">
          <main>
            <DynamicForm
              formName={`Employee${
                formData?.id ? " — APITCO/" + formData.id : ""
              }`}
              formConfig={employeeFormConfig}
              formData={formData}
              onChange={handleChange}
              onSubmit={handleSave}
              onBack={() => {}}
              isEditing={!!formData.id}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              states={states}
              departments={departments}
              designations={designations}
              tabs={employeeFormTabs}
              errors={errors}
              files={files}
              setFiles={setFiles}
              onUpload={handleFileUpload}
              onDownload={handleFileDownload}
              validateTab={validateForm} 
            />
          </main>
        </div>
      </div>
    </div>
  );
}
